/**
 * Enumeration of all supported figure variable setting types.
 * Used to discriminate between different variable setting shapes.
 */
export enum FigureVarTypeDto {
  list = "list",
  number = "number",
  string = "string",
  select = "select",
  variable = "variable",
}

/**
 * Defines conditional requirements for a figure variable setting.
 * Determines when a particular variable setting should be displayed or enabled
 * based on the value of another variable.
 */
export interface FigureVarRequiresDto {
  /** The name of the variable that must match for this setting to be applicable. */
  varName: string;
  /** Collection of values that the named variable must have for this setting to be active. */
  values: string[];
}

/**
 * Defines a select-type figure variable setting with a fixed list of options.
 */
export interface FigureVarSelectSettingsDto {
  /** Discriminant tag identifying this as a select variable. */
  type: FigureVarTypeDto.select;
  /** Optional pre-selected default value. */
  defaultValue?: string;
  /** Human-readable label shown alongside the variable control. */
  label: string;
  /** Available options for the select control. */
  options: string[];
  /** Unique variable name used to reference this setting. */
  varName: string;
  /** Optional conditional requirement that controls when this setting is active. */
  requires?: FigureVarRequiresDto;
}

/**
 * Defines a standard figure variable setting such as a string, number, list, or variable reference.
 */
export interface FigureVarStandardSettingsDto {
  /** Discriminant tag identifying this as a standard variable type. */
  type:
    | FigureVarTypeDto.list
    | FigureVarTypeDto.number
    | FigureVarTypeDto.string
    | FigureVarTypeDto.variable;
  /** Optional default value for the variable. */
  defaultValue?: string;
  /** Human-readable label shown alongside the variable control. */
  label: string;
  /** Unique variable name used to reference this setting. */
  varName: string;
  /** Optional conditional requirement that controls when this setting is active. */
  requires?: FigureVarRequiresDto;
}

/**
 * Union type representing any figure variable setting, either select or standard.
 */
export type FigureVarSettingsDto = FigureVarSelectSettingsDto | FigureVarStandardSettingsDto;

/**
 * Type guard to check if an object is a FigureVarSettingsDto value.
 *
 * @param obj - The object to check.
 * @returns True when the value matches one of the supported figure variable setting shapes.
 */
export const isFigureVarSettingsDto = (obj: unknown): obj is FigureVarSettingsDto => {
  if (typeof obj !== "object" || obj === null) {
    return false;
  }
  const chkObj = obj as FigureVarSettingsDto;
  if (typeof chkObj.varName !== "string") {
    return false;
  }
  if (typeof chkObj.label !== "string") {
    return false;
  }
  if (
    chkObj.type !== FigureVarTypeDto.list &&
    chkObj.type !== FigureVarTypeDto.number &&
    chkObj.type !== FigureVarTypeDto.string &&
    chkObj.type !== FigureVarTypeDto.select &&
    chkObj.type !== FigureVarTypeDto.variable
  ) {
    return false;
  }
  if (chkObj.type === FigureVarTypeDto.select) {
    const selectObj = chkObj as FigureVarSelectSettingsDto;
    if (
      !Array.isArray(selectObj.options) ||
      selectObj.options.some((option) => typeof option !== "string")
    ) {
      return false;
    }
  }
  if (chkObj.type !== FigureVarTypeDto.select && "options" in chkObj) {
    return false;
  }
  if ("requires" in chkObj && chkObj.requires !== undefined) {
    if (typeof chkObj.requires !== "object" || chkObj.requires === null) {
      return false;
    }
    const requires = chkObj.requires as FigureVarRequiresDto;
    if (typeof requires.varName !== "string") {
      return false;
    }
    if (
      !Array.isArray(requires.values) ||
      requires.values.some((value) => typeof value !== "string")
    ) {
      return false;
    }
  }
  return true;
};
